import { Container, DataGridTable, Spinner } from 'components'
import { riskDashboard, applyCommercialAutoVisibility } from 'constants/tableData'
import styles from './riskDetails.module.scss'
import { useNavigate } from 'react-router'
import { useGridApiRef } from '@mui/x-data-grid'
import { useEffect, useState, useMemo, useRef } from 'react'
import FleetColumnHeader from './FleetColumnHeader'
import strings from 'strings'
import { exportTableAsCSV } from 'tools/helper'
import { RiskScoreDashboardDetail, CustomPagination } from '../../types/riskDashboard'

const t = strings.TABLE_DEFAULTS
const e = strings.CSV_EXPORT

interface IRiskDetailsProps {
    print: boolean,
    isLoading: boolean,
    detailsData: RiskScoreDashboardDetail[],
    isAgent: boolean,
    commercialAuto?: boolean,
    pagination?: CustomPagination,
    onPageChange?: (page: number, size: number) => void,
    onSearchChange?: (search: string) => void,
    onSortChange?: (field: string, direction: string) => void,
    currentPage?: number,
    pageSize?: number,
    minSearchLength?: number,
    maxSearchLength?: number
}

// Fields that are allowed to be sortable
const ALLOWED_SORTABLE_FIELDS = [
    'totalMiles',
    'fleetScore',
    'geoIndex',
    'speedingIndex',
    'safetyIndex',
    'executionDateFormatted',
    'fleetName'
]

const RiskDetails: React.FC<IRiskDetailsProps> = (props) => {
    const {
        print,
        isLoading,
        detailsData,
        isAgent,
        commercialAuto,
        pagination,
        onPageChange,
        onSearchChange,
        onSortChange,
        currentPage = 0,
        pageSize = 100,
        minSearchLength,
        maxSearchLength
    } = props
    const { riskColumns, riskGroups } = useMemo(() => {
        const { columns, groups } = applyCommercialAutoVisibility(
            riskDashboard.riskColumns,
            riskDashboard.riskGroups,
            commercialAuto
        )
        return { riskColumns: columns, riskGroups: groups }
    }, [commercialAuto])
    const navigate = useNavigate()
    const apiRef = useGridApiRef()

    const [riskColumnsState, setRiskColumnsState] = useState<any>(riskColumns)
    const [riskGroupsState, setRiskGroupsState] = useState<any>(riskGroups)
    const [loadingRiskTable, setLoadingRiskTable] = useState<boolean>(true)
    // Keep the whole table (headers included) in a loading state until the first
    // data load completes, so the flag-dependent headers don't flash/change.
    const [columnsReady, setColumnsReady] = useState<boolean>(false)
    const wasLoadingRef = useRef<boolean>(false)
    const [visibleCodeTypes, setVisibleCodeTypes] = useState<string[]>([])
    const [availableCodeTypes, setAvailableCodeTypes] = useState<string[]>([])
    const [searchWarning, setSearchWarning] = useState<string>('')

    useEffect(() => {
        if (isAgent) {
            // TRUCK-6309: through the Speeding group (14 cols with 85+, 13 without); 6 groups = up to Speeding (excl. Geographic)
            setRiskColumnsState(riskColumns.slice(0, commercialAuto ? 14 : 13))
            setRiskGroupsState(riskGroups.slice(0, 6))
        }

        const updatedColumns = riskColumns.map(column => {
            const updatedColumn = { ...column }

            // Set sortable based on allowed fields
            updatedColumn.sortable = ALLOWED_SORTABLE_FIELDS.includes(column.field)

            if (column.field === 'fleetName') {
                updatedColumn.renderHeader = () => (
                    <FleetColumnHeader
                        availableCodeTypes={availableCodeTypes}
                        selectedTypes={visibleCodeTypes}
                        onFilterChange={handleCodeTypeFilterChange}
                    />
                )
            }
            return updatedColumn
        })
        setRiskColumnsState(updatedColumns)
        setLoadingRiskTable(false)
    }, [availableCodeTypes, visibleCodeTypes, commercialAuto])

    useEffect(() => {
        // Reveal the table only after the first data load finishes (flag is known
        // by then), and keep it visible on subsequent refetches.
        if (wasLoadingRef.current && !isLoading) {
            setColumnsReady(true)
        }
        wasLoadingRef.current = isLoading
    }, [isLoading])

    useEffect(() => {
        const types = new Set<string>()
        detailsData?.forEach((row) => {
            row?.carrierCodes?.forEach((code) => {
                if (code.codeType) {
                    types.add(code.codeType)
                }
            })
            if (row?.policyId) {
                types.add('POLICY_ID')
            }
        })
        const uniqueTypes = Array.from(types)
        setAvailableCodeTypes(uniqueTypes)
        setVisibleCodeTypes(uniqueTypes)
    }, [detailsData])

    const handleCodeTypeFilterChange = (selectedTypes: string[]) => {
        setVisibleCodeTypes(selectedTypes)
    }

    const handleQuickFilterChange = (value: string) => {
        if (value.length === 0) {
            setSearchWarning('')
            onSearchChange?.('')
        } else if (minSearchLength !== undefined && value.length < minSearchLength) {
            setSearchWarning(`Please enter at least ${minSearchLength} characters to search`)
            onSearchChange?.('')
        } else if (maxSearchLength !== undefined && value.length > maxSearchLength) {
            setSearchWarning(`Please enter at most ${maxSearchLength} characters to search`)
        } else {
            setSearchWarning('')
            onSearchChange?.(value)
        }
    }

    const handlePaginationChange = (page: number, size: number) => {
        if (onPageChange) {
            onPageChange(page, size)
        }
    }


    riskColumns[0].handleClick = (row: RiskScoreDashboardDetail) => {
        const originalRow = detailsData?.find((item) =>
            item.eldProvider === row.eldProvider &&
            item.companyEldAuthenticationId === row.companyEldAuthenticationId
        )

        if (!originalRow) {
            return
        }

        const dot = originalRow.carrierCodes?.find((item) => item.codeType === 'DOT' && item.codeValue !== t.DASH_VALUE)
        const mc = originalRow.carrierCodes?.find((item) => item.codeType === 'MC' && item.codeValue !== t.DASH_VALUE)
        const uuid = originalRow.carrierCodes?.find((item) => item.codeType === 'UUID' && item.codeValue !== t.DASH_VALUE)

        if (dot) {
            localStorage.setItem('carrierCodeType', 'DOT')
            localStorage.setItem('carrierCodeValue', dot.codeValue as string)
        }
        else if (mc) {
            localStorage.setItem('carrierCodeType', 'MC')
            localStorage.setItem('carrierCodeValue', mc.codeValue as string)
        }
        else if (uuid) {
            localStorage.setItem('carrierCodeType', 'UUID')
            localStorage.setItem('carrierCodeValue', uuid.codeValue as string)
        }
        else {
            localStorage.removeItem('carrierCodeType')
            localStorage.removeItem('carrierCodeValue')
        }

        localStorage.setItem('carrierPolicyId', originalRow.policyId || '')
        localStorage.setItem('carrierName', originalRow.fleetName as string)
        localStorage.setItem('eldProviderRisk', originalRow.eldProvider as string)
        localStorage.setItem('onboardingDate', originalRow.onboardedDate || '')
        localStorage.setItem('carrierStatus', originalRow.credentialStatus || '')
        originalRow.carrierId ? localStorage.setItem('carrierId', String(originalRow.carrierId)) : localStorage.removeItem('carrierId')
        navigate('../carriers/details')
    }

    const filteredData = useMemo(() => {
        // With server-side pagination, no need for client-side search
        // Just filter by visible code types
        return detailsData?.map((row) => {
            const filteredCodes = row.carrierCodes?.filter((code) =>
                code.codeType != null && visibleCodeTypes.includes(code.codeType)
            ) || []

            return {
                ...row,
                carrierCodes: filteredCodes,
                policyId: visibleCodeTypes.includes('POLICY_ID') ? row.policyId : undefined
            }
        })
    }, [detailsData, visibleCodeTypes])

    const handleRiskExport = () => {
        const csvString = apiRef.current.getDataAsCsv()
        const csvRows = csvString.split('\n')
        const modifiedCsv = csvRows.slice(1).join('\n')
        exportTableAsCSV(modifiedCsv, e.RISK_CSV_EXPORT_NAME)
    }

    const tableMenuItems = [
        {
            label: e.DOWNLOAD_AS_CSV,
            key: 'export',
            action: () => {
                handleRiskExport()
            },
        },
    ]

    return (
        <>
            {(!loadingRiskTable && columnsReady) ? (
                <Container
                    className={`${styles.riskDetailsContainer} ${print ? styles.printContainer : ''
                        }`}
                >
                    {searchWarning && (
                        <div style={{
                            padding: '8px 16px',
                            backgroundColor: '#fff3cd',
                            color: '#856404',
                            borderRadius: '4px',
                            marginBottom: '8px',
                            fontSize: '14px'
                        }}>
                            {searchWarning}
                        </div>
                    )}
                    <DataGridTable
                        data={filteredData}
                        columns={riskColumnsState}
                        columnGroupingModel={riskGroupsState}
                        columnHeaderHeight={30}
                        getRowId={(row: any) => row?.fleetName + '|' + row?.eldProvider + '|' + row?.companyEldAuthenticationId}
                        reduceHeight={320}
                        rowHeight={65}
                        showTitle={false}
                        showFilter={true}
                        multiSearchFilter={false}
                        checkboxSelection={false}
                        loading={isLoading}
                        autoHeight={print}
                        rowsPerPageOptions={[5, 10, 20, 50, 100]}
                        defaultPageSize={pageSize}
                        currentPage={currentPage}
                        pageSize={pageSize}
                        sortField={'totalMiles'}
                        sortOrder={'desc'}
                        menuList={tableMenuItems}
                        apiRef={apiRef}
                        onSingleQuickFilterChange={handleQuickFilterChange}
                        paginationMode="server"
                        totalCount={pagination?.totalElements || 0}
                        handlePagination={handlePaginationChange}
                        onSortChange={onSortChange}
                    />
                </Container>
            ) : (
                <Container className={styles.riskDetailsContainer}>
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            minHeight: '320px',
                        }}
                    >
                        <Spinner />
                    </div>
                </Container>
            )}
        </>
    )
}

export default RiskDetails